import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { StudioId } from '@/domain/types';
import type { ReportingScope } from '@/domain/reportingCompare';
import { singleStudioScope } from '@/domain/reportingCompare';
import { Money } from '@/components/reporting/Money';
import InfoTip from '@/components/InfoTip';
import {
  targetBoard,
  depositSessionsCount,
  studioDepositSessionsTarget,
  newClientsByArtistWeek,
  type TargetBoard,
  type TargetCell,
} from '@/domain/reportingEngine';
import { leadThresholdBand, LEAD_BAND_LABEL, type LeadThresholdBand } from '@/data/reportingTargets';

const BAND_CLASS: Record<LeadThresholdBand, string> = {
  meet: 'text-cream-soft',
  acceptable: 'text-gold-bright',
  'very-good': 'text-mint',
};

function dayLabel(dayKey: string): string {
  return new Date(`${dayKey}T12:00:00+10:00`).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
  });
}

function paceClass(ok: boolean): string {
  return ok ? 'text-mint' : 'text-blood';
}

function cellSessionClass(cell: TargetCell): string {
  if (!cell.elapsed) return 'text-cream-faint/40';
  if (cell.sessions === 0) return 'bg-blood/10 text-cream-faint';
  return cell.onPace ? 'bg-mint/20 text-cream' : 'bg-blood/20 text-cream';
}

interface StudioBoardData {
  board: TargetBoard;
  depositSessions: number;
  depositSessionsTarget: number;
  newClients: Record<string, number>;
}

function boardDataFor(studioId: StudioId, now: string): StudioBoardData {
  const board = targetBoard(studioId, now);
  return {
    board,
    depositSessions: depositSessionsCount(board.days, studioId),
    depositSessionsTarget: studioDepositSessionsTarget(studioId),
    newClients: newClientsByArtistWeek(board.days, studioId),
  };
}

/** The Targets tab (real data): per-studio artist x day board vs seeded weekly
 *  targets. `targetBoard` is inherently single-studio (it derives its own target
 *  week), so with no studio selected we default to the first active studio and
 *  say so; with 2-3 selected (compare mode) we stack one board per studio. */
export default function TargetsTab({ scope }: { scope: ReportingScope }) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const artists = useBrainStore((s) => s.artists);
  const customers = useBrainStore((s) => s.customers);
  const deposits = useBrainStore((s) => s.deposits);
  const reportingTargets = useBrainStore((s) => s.reportingTargets);
  const { studioIds, compareMode, moneyVisible } = scope;

  const activeStudioIds = useMemo(
    () => Object.values(studios).filter((s) => !s.archived).map((s) => s.id),
    [studios],
  );

  const single = singleStudioScope(studioIds);
  const defaultStudioId = !compareMode && !single ? activeStudioIds[0] : undefined;
  const resolvedStudioIds: StudioId[] = compareMode
    ? [...studioIds]
    : single
      ? [single]
      : defaultStudioId
        ? [defaultStudioId]
        : [];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const boards = useMemo(
    () => resolvedStudioIds.map((sid) => ({ studioId: sid, data: boardDataFor(sid, clockIso) })),
    [resolvedStudioIds.join(','), clockIso, bookings, salePayments, artists, customers, deposits, reportingTargets],
  );

  if (boards.length === 0) {
    return (
      <div className="panel p-8 max-w-3xl" data-testid="reporting-targets">
        <p className="font-serif text-cream-soft">No studios to show.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-targets">
      {defaultStudioId ? (
        <p
          className="font-inter text-[11px] text-cream-faint uppercase tracking-widest"
          data-testid="targets-default-note"
        >
          Targets are per studio — showing {studios[defaultStudioId]?.name ?? defaultStudioId}.
        </p>
      ) : null}
      {boards.map(({ studioId, data }) => (
        <StudioBoard
          key={studioId}
          studioId={studioId}
          studioName={studios[studioId]?.name ?? studioId}
          data={data}
          moneyVisible={moneyVisible}
          showHeading={boards.length > 1}
        />
      ))}
    </div>
  );
}

function StudioBoard({
  studioId,
  studioName,
  data,
  moneyVisible,
  showHeading,
}: {
  studioId: StudioId;
  studioName: string;
  data: StudioBoardData;
  moneyVisible: boolean;
  showHeading: boolean;
}) {
  const { board, depositSessions, depositSessionsTarget, newClients } = data;

  return (
    <section className="space-y-3">
      {showHeading ? <h2 className="heading-serif text-lg text-cream">{studioName}</h2> : null}

      {/* On-pace tiles */}
      <div className="grid grid-cols-3 gap-3">
        <div className="panel-dark p-4" data-testid={`targets-pace-sessions-${studioId}`}>
          <p className="label-small-caps flex items-center gap-1">
            Sessions this week
            <InfoTip>
              Completed sessions so far this target week vs the studio&apos;s weekly session target.
              &quot;Should be at&quot; pro-rates the target by elapsed days.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', paceClass(board.totals.sessions >= board.totals.expectedSessionsByNow))}>
            {board.totals.sessions}
            <span className="text-cream-faint text-lg"> / {board.totals.targetSessions}</span>
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            Should be at {Math.round(board.totals.expectedSessionsByNow)} by now
          </p>
        </div>
        <div className="panel-dark p-4" data-testid={`targets-pace-dollars-${studioId}`}>
          <p className="label-small-caps flex items-center gap-1">
            Revenue this week
            <InfoTip>
              Completed-session dollars so far this target week vs the weekly dollar target, pro-rated the same way.
            </InfoTip>
          </p>
          <p className={clsx('font-cinzel text-3xl mt-2', paceClass(board.totals.dollarsAud >= board.totals.expectedDollarsByNow))}>
            <Money amountAud={board.totals.dollarsAud} visible={moneyVisible} />
          </p>
          <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
            Target <Money amountAud={board.totals.targetDollarsAud} visible={moneyVisible} />
          </p>
        </div>
        <div className="panel-dark p-4" data-testid={`targets-deposit-sessions-${studioId}`}>
          <p className="label-small-caps flex items-center gap-1">
            Deposit sessions
            <InfoTip>
              Booked sessions this week backed by a deposit vs the studio&apos;s target (sum of its artists&apos;
              targets).
            </InfoTip>
          </p>
          <p
            className={clsx(
              'font-cinzel text-3xl mt-2',
              depositSessionsTarget > 0 && depositSessions >= depositSessionsTarget
                ? 'text-mint'
                : depositSessionsTarget > 0
                  ? 'text-blood'
                  : 'text-gold-bright',
            )}
          >
            {depositSessions}
            {depositSessionsTarget > 0 ? <span className="text-cream-faint text-lg"> / {depositSessionsTarget}</span> : null}
          </p>
        </div>
      </div>

      {/* Artist x day board */}
      <div className="panel p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`targets-board-${studioId}`}>
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                <th className="text-left px-2 py-2">Artist</th>
                {board.days.map((d) => (
                  <th key={d} className="text-center px-1 py-2 whitespace-nowrap">
                    {dayLabel(d)}
                  </th>
                ))}
                <th className="text-right px-2 py-2">Avg job</th>
                <th className="text-right px-2 py-2">New</th>
                <th className="text-right px-2 py-2">Sessions</th>
                <th className="text-right px-2 py-2">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {board.rows.map((row) => (
                <tr key={row.artistId} className="border-t border-gold-dim/15">
                  <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{row.name}</td>
                  {row.cells.map((cell) => (
                    <td key={cell.dayIso} className="px-1 py-1 text-center">
                      <div
                        className={clsx('rounded-sm py-1 px-0.5', cellSessionClass(cell))}
                        title={`${row.name} · ${dayLabel(cell.dayIso)}: ${cell.sessions} sessions`}
                      >
                        <div className="font-mono text-sm leading-none">
                          {cell.elapsed || cell.sessions > 0 ? cell.sessions : '·'}
                        </div>
                        <div className="font-mono text-[9px] text-cream-soft leading-none mt-0.5">
                          {cell.dollarsAud > 0 ? <Money amountAud={cell.dollarsAud} visible={moneyVisible} /> : ''}
                        </div>
                      </div>
                    </td>
                  ))}
                  <td
                    className={clsx('px-2 py-2 text-right font-mono', row.avgJobValueAud > 0 ? 'text-gold-bright' : 'text-cream-faint')}
                    data-testid={`targets-avgjob-${row.artistId}`}
                  >
                    {row.avgJobValueAud > 0 ? <Money amountAud={row.avgJobValueAud} visible={moneyVisible} /> : '—'}
                  </td>
                  <td
                    className={clsx('px-2 py-2 text-right font-mono', BAND_CLASS[leadThresholdBand(newClients[row.artistId] ?? 0)])}
                    data-testid={`targets-newclients-${row.artistId}`}
                  >
                    {newClients[row.artistId] ?? 0}
                    <span className="text-cream-faint text-[9px] uppercase tracking-widest ml-1">
                      {LEAD_BAND_LABEL[leadThresholdBand(newClients[row.artistId] ?? 0)]}
                    </span>
                  </td>
                  <td className={clsx('px-2 py-2 text-right font-mono', paceClass(row.sessionsOnPace))}>
                    {row.sessions}
                    <span className="text-cream-faint"> / {row.targetSessions}</span>
                  </td>
                  <td className={clsx('px-2 py-2 text-right font-mono', paceClass(row.dollarsOnPace))}>
                    <Money amountAud={row.dollarsAud} visible={moneyVisible} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gold-dim/30 text-cream">
                <td className="px-2 py-2 label-small-caps text-gold-bright">All artists</td>
                <td colSpan={board.days.length} />
                <td className="px-2 py-2" />
                <td className="px-2 py-2" />
                <td className={clsx('px-2 py-2 text-right font-mono', paceClass(board.totals.sessions >= board.totals.expectedSessionsByNow))}>
                  {board.totals.sessions} / {board.totals.targetSessions}
                </td>
                <td className={clsx('px-2 py-2 text-right font-mono', paceClass(board.totals.dollarsAud >= board.totals.expectedDollarsByNow))}>
                  <Money amountAud={board.totals.dollarsAud} visible={moneyVisible} />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-3">
          Green = on pace vs target · red = behind · avg job value is the $20k-shortfall catcher
        </p>
      </div>
    </section>
  );
}
